/**
 * 格式化Markdown文本为HTML
 * @param {string} text Markdown文本
 * @returns {string} 格式化后的HTML
 */
export function formatMarkdown(text) {
  if (!text) return ''

  // 处理代码块
  text = text.replace(/```([\s\S]*?)```/g, (match, codeContent) => {
    // 检测语言
    const languageMatch = codeContent.match(/^([a-zA-Z0-9_+-]+)\n/)
    let language = ''
    let code = codeContent

    if (languageMatch) {
      language = languageMatch[1]
      code = codeContent.substring(languageMatch[0].length)
    }

    // 应用简单的语法高亮
    if (language) {
      code = highlightCode(code, language)
    }

    return `<pre class="code-block${language ? ' language-' + language : ''}"><code>${escapeHtml(code)}</code></pre>`
  })

  // 处理行内代码
  text = text.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>')

  // 处理标题
  text = text.replace(/^### (.*$)/gm, '<h3>$1</h3>')
  text = text.replace(/^## (.*$)/gm, '<h2>$1</h2>')
  text = text.replace(/^# (.*$)/gm, '<h1>$1</h1>')

  // 处理粗体
  text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

  // 处理斜体
  text = text.replace(/\*(.*?)\*/g, '<em>$1</em>')

  // 处理链接
  text = text.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')

  // 处理表格
  if (text.indexOf('|') !== -1) {
    const lines = text.split('\n')
    let inTable = false
    let tableHtml = '<div class="table-container"><table>'
    let tableRows = []

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim()

      if (line.startsWith('|') && line.endsWith('|')) {
        if (!inTable) {
          inTable = true
        }

        tableRows.push(line)
      } else if (inTable) {
        // 表格结束
        inTable = false

        // 处理表头
        if (tableRows.length >= 2) {
          const headers = tableRows[0].split('|').filter(cell => cell.trim() !== '')
          tableHtml += '<thead><tr>'

          for (const header of headers) {
            tableHtml += `<th>${header.trim()}</th>`
          }

          tableHtml += '</tr></thead><tbody>'

          // 处理数据行
          for (let r = 2; r < tableRows.length; r++) {
            const cells = tableRows[r].split('|').filter(cell => cell.trim() !== '')
            tableHtml += '<tr>'

            for (const cell of cells) {
              tableHtml += `<td>${cell.trim()}</td>`
            }

            tableHtml += '</tr>'
          }

          tableHtml += '</tbody></table></div>'

          // 替换原文本中的表格
          const tableText = tableRows.join('\n')
          text = text.replace(tableText, tableHtml)
        }

        tableRows = []
      }
    }

    // 处理文档末尾的表格
    if (inTable && tableRows.length >= 2) {
      const headers = tableRows[0].split('|').filter(cell => cell.trim() !== '')
      tableHtml += '<thead><tr>'

      for (const header of headers) {
        tableHtml += `<th>${header.trim()}</th>`
      }

      tableHtml += '</tr></thead><tbody>'

      // 处理数据行
      for (let r = 2; r < tableRows.length; r++) {
        const cells = tableRows[r].split('|').filter(cell => cell.trim() !== '')
        tableHtml += '<tr>'

        for (const cell of cells) {
          tableHtml += `<td>${cell.trim()}</td>`
        }

        tableHtml += '</tr>'
      }

      tableHtml += '</tbody></table></div>'

      // 替换原文本中的表格
      const tableText = tableRows.join('\n')
      text = text.replace(tableText, tableHtml)
    }
  }

  // 处理列表
  text = text.replace(/^\s*[-*]\s+(.*)/gm, '<li>$1</li>')
  let hasUnorderedList = text.includes('<li>')
  if (hasUnorderedList) {
    text = text.replace(/(<li>.*?<\/li>)(?:\s*<li>.*?<\/li>)*/gs, '<ul>$&</ul>')
  }

  // 处理有序列表
  text = text.replace(/^\s*(\d+)\.\s+(.*)/gm, '<li>$2</li>')
  let hasOrderedList = text.includes('<li>') && !hasUnorderedList
  if (hasOrderedList) {
    text = text.replace(/(<li>.*?<\/li>)(?:\s*<li>.*?<\/li>)*/gs, '<ol>$&</ol>')
  }

  // 处理段落
  text = text.replace(/\n\s*\n/g, '</p><p>')
  text = '<p>' + text + '</p>'
  text = text.replace(/<p><\/p>/g, '')

  // 处理换行
  text = text.replace(/\n/g, '<br>')

  return text
}

/**
 * 代码语法高亮
 * @param {string} code 代码内容
 * @param {string} language 语言
 * @returns {string} 高亮后的代码
 */
function highlightCode(code, language) {
  // 简单的语法高亮，为关键词添加类
  const keywords = {
    // JavaScript关键词
    'js': ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'export', 'async', 'await', 'try', 'catch'],
    // Python关键词
    'python': ['def', 'class', 'import', 'from', 'return', 'if', 'elif', 'else', 'for', 'while', 'try', 'except', 'with', 'as', 'and', 'or', 'not', 'in', 'is'],
    // Java关键词
    'java': ['public', 'private', 'protected', 'class', 'interface', 'extends', 'implements', 'static', 'final', 'void', 'return', 'if', 'else', 'for', 'while', 'try', 'catch'],
    // 通用关键词
    'default': ['function', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'export']
  }

  // 映射语言别名
  const languageMap = {
    'javascript': 'js',
    'typescript': 'js',
    'jsx': 'js',
    'tsx': 'js',
    'py': 'python',
    'java': 'java'
  }

  const mappedLang = languageMap[language.toLowerCase()] || 'default'
  const relevantKeywords = keywords[mappedLang] || keywords['default']

  // 替换关键词
  relevantKeywords.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'g')
    code = code.replace(regex, `<span class="hljs-keyword">${keyword}</span>`)
  })

  // 处理字符串
  code = code.replace(/"(.*?)"/g, '<span class="hljs-string">"$1"</span>')
  code = code.replace(/'(.*?)'/g, '<span class="hljs-string">\'$1\'</span>')

  // 处理数字
  code = code.replace(/\b(\d+)\b/g, '<span class="hljs-number">$1</span>')

  // 处理注释
  code = code.replace(/\/\/(.*?)$/gm, '<span class="hljs-comment">//$1</span>')
  code = code.replace(/\/\*([\s\S]*?)\*\//g, '<span class="hljs-comment">/*$1*/</span>')
  code = code.replace(/#(.*?)$/gm, '<span class="hljs-comment">#$1</span>')

  return code
}

/**
 * 转义HTML特殊字符
 * @param {string} unsafe 不安全的字符串
 * @returns {string} 转义后的字符串
 */
export function escapeHtml(unsafe) {
  if (!unsafe) return ''

  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}
